/**
 * Utility functions for calculating optimal hedge price
 */

/**
 * Calculate the optimal hedge price based on closed positions PNL and open positions parameters
 *
 * @param {Array} positions - Array of position objects
 * @returns {number|null} - The optimal hedge price or null if cannot be calculated
 */
export function calculateOptimalHedgePrice(positions) {
  if (!positions || positions.length === 0) {
    return null;
  }

  // Step 1: Count sum of final PNLs [USDT] of all closed positions
  let all_PNL = 0;
  const closedPositions = positions.filter(pos => pos.position?.status === 'closed');

  for (const pos of closedPositions) {
    const pnlUsdt = pos.position?.actualPnlUsdt;
    if (pnlUsdt !== '---' && pnlUsdt !== undefined && pnlUsdt !== null) {
      all_PNL += parseFloat(pnlUsdt);
    }
  }

  // Step 2: Process all opened positions
  const openPositions = positions.filter(pos => pos.position?.status === 'open');

  if (openPositions.length === 0) {
    // No open positions, cannot calculate optimal hedge price
    return null;
  }

  let numeratorSum = (-1) * all_PNL;
  let denominatorSum = 0;

  for (const pos of openPositions) {
    const contract = (pos.contract || '').toUpperCase();
    const openingPriceRaw = parseFloat(pos.position?.openingPriceRaw);
    const openingFee = parseFloat(pos.position?.openingFee) || 0;
    const openingVolume = parseFloat(pos.position?.openingVolumeRaw);
    const takerFeePercent = parseFloat(pos.takerFee) || 0;

    // Validate all required values
    if (isNaN(openingPriceRaw) || isNaN(openingVolume) || openingVolume === 0) {
      console.warn('Invalid data for position, skipping:', pos);
      continue;
    }

    // short = -1 if SHORT, otherwise 1
    const short = contract === 'SHORT' ? -1 : 1;

    // paidfor = (opening price + opening fee) * volume
    const paidfor = (openingPriceRaw + openingFee) * openingVolume;

    // fee as decimal (e.g., 0.1% = 0.001)
    const fee = takerFeePercent / 100;

    // volume
    const volume = openingVolume;

    // Add to sums
    numeratorSum += short * paidfor;
    denominatorSum += short * (1 - fee) * volume;
  }

  // Step 3: Calculate optimal hedge price
  if (denominatorSum === 0) {
    console.warn('Denominator is zero, cannot calculate optimal hedge price');
    return null;
  }

  const optimal_hedge_price = numeratorSum / denominatorSum;

  return optimal_hedge_price;
}

/**
 * Get optimal number of decimal places for a value
 *
 * @param {number} maxDigits - Maximum number of digits to display
 * @param {number} value - The value to format
 * @param {number} minDecimalPlaces - Minimum decimal places to show
 * @returns {number} - Number of decimal places to use
 */
export function getOptimalDecimalPlaces(maxDigits, value, minDecimalPlaces = 2) {
  if (value === null || value === undefined || isNaN(value)) {
    return minDecimalPlaces;
  }

  const absValue = Math.abs(value);

  if (absValue === 0) {
    return minDecimalPlaces;
  }

  // Count digits before decimal point
  const integerDigits = Math.floor(Math.log10(absValue)) + 1;

  // Calculate available decimal places
  const availableDecimals = Math.max(minDecimalPlaces, maxDigits - integerDigits);

  return availableDecimals;
}
